'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useMessage, useMessageIds, useConversations } from '@mui/x-chat-headless';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ChatLayout, useChatLocaleText, useChatVariant } from '@mui/x-chat-headless';
import { styled } from "../internals/zero-styled/index.mjs";
import { ChatConversation } from "../ChatConversation/ChatConversation.mjs";
import { ChatConversationHeader } from "../ChatConversation/ChatConversationHeader.mjs";
import { ChatConversationTitle } from "../ChatConversation/ChatConversationTitle.mjs";
import { ChatConversationSubtitle } from "../ChatConversation/ChatConversationSubtitle.mjs";
import { ChatConversationHeaderInfo } from "../ChatConversation/ChatConversationHeaderInfo.mjs";
import { ChatConversationHeaderActions } from "../ChatConversation/ChatConversationHeaderActions.mjs";
import { ChatConversationList } from "../ChatConversationList/ChatConversationList.mjs";
import { ChatComposer } from "../ChatComposer/ChatComposer.mjs";
import { ChatComposerTextArea } from "../ChatComposer/ChatComposerTextArea.mjs";
import { ChatComposerSendButton } from "../ChatComposer/ChatComposerSendButton.mjs";
import { ChatComposerAttachButton } from "../ChatComposer/ChatComposerAttachButton.mjs";
import { ChatComposerAttachmentList } from "../ChatComposer/ChatComposerAttachmentList.mjs";
import { ChatComposerToolbar } from "../ChatComposer/ChatComposerToolbar.mjs";
import { ChatComposerHelperText } from "../ChatComposer/ChatComposerHelperText.mjs";
import { ChatMessageList } from "../ChatMessageList/ChatMessageList.mjs";
import { ChatMessageGroup } from "../ChatMessage/ChatMessageGroup.mjs";
import { ChatMessageContent } from "../ChatMessage/ChatMessageContent.mjs";
import { ChatMessageMeta } from "../ChatMessage/ChatMessageMeta.mjs";
import { ChatMessageAvatar } from "../ChatMessage/ChatMessageAvatar.mjs";
import { ChatMessage } from "../ChatMessage/ChatMessage.mjs";
import { ChatMessageActions } from "../ChatMessage/ChatMessageActions.mjs";
import { ChatMessageInlineMeta } from "../ChatMessage/ChatMessageInlineMeta.mjs";
import { ChatScrollToBottomAffordance } from "../ChatIndicators/ChatScrollToBottomAffordance.mjs";
import { ChatSuggestions } from "../ChatSuggestions/ChatSuggestions.mjs";
import DefaultSendIcon from "../icons/DefaultSendIcon.mjs";
import DefaultAttachIcon from "../icons/DefaultAttachIcon.mjs";
import DefaultMenuIcon from "../icons/DefaultMenuIcon.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const NARROW_BREAKPOINT = 600;

/**
 * Observes the ChatBox root element's inline size and returns `true`
 * when it is narrower than the breakpoint. This mirrors the
 * `@container (max-width: 599.95px)` rule used in CSS so the JS
 * side can show/hide the drawer and menu button in sync.
 */
function useContainerNarrow(ref) {
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof globalThis.ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new globalThis.ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        setNarrow(width < NARROW_BREAKPOINT);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return narrow;
}
const ChatBoxEmptyState = styled('div', {
  name: 'MuiChatBox',
  slot: 'EmptyState'
})(({
  theme
}) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  color: (theme.vars || theme).palette.text.secondary,
  padding: theme.spacing(4),
  userSelect: 'none',
  pointerEvents: 'none'
}));
const ChatBoxEmptyStateIcon = styled('svg', {
  name: 'MuiChatBox',
  slot: 'EmptyStateIcon'
})(({
  theme
}) => ({
  width: 48,
  height: 48,
  color: (theme.vars || theme).palette.action.disabled,
  marginBottom: theme.spacing(1)
}));
const ChatBoxEmptyStateTitle = styled('p', {
  name: 'MuiChatBox',
  slot: 'EmptyStateTitle'
})(({
  theme
}) => _extends({
  margin: 0
}, theme.typography.subtitle1, {
  color: (theme.vars || theme).palette.text.secondary
}));
const ChatBoxEmptyStateHelper = styled('p', {
  name: 'MuiChatBox',
  slot: 'EmptyStateHelper'
})(({
  theme
}) => _extends({
  margin: 0
}, theme.typography.body2, {
  color: (theme.vars || theme).palette.text.disabled
}));
function DefaultMessageItem({
  id,
  slots,
  slotProps
}) {
  const variant = useChatVariant();
  const message = useMessage(id);
  const MessageGroupComponent = slots?.messageGroup ?? ChatMessageGroup;
  const MessageAvatarComponent = slots?.messageAvatar ?? ChatMessageAvatar;
  const MessageContentComponent = slots?.messageContent ?? ChatMessageContent;
  const MessageMetaComponent = slots?.messageMeta ?? ChatMessageMeta;
  const MessageRootComponent = slots?.messageRoot ?? ChatMessage;
  const MessageActionsSlot = slots?.messageActions;
  const isDefault = variant !== 'compact';
  const isStreaming = message?.status === 'streaming';

  // Default variant: inline meta inside the bubble (Telegram-style).
  // Skip during streaming — there is no timestamp yet, and the streaming state
  // is already communicated via the MuiChatMessage-streaming CSS class.
  // Also skip when the message carries no displayable meta at all (no timestamp,
  // no edited label, no delivery status) so the spacer does not add dead space.
  const hasMeta = Boolean(message?.createdAt) || Boolean(message?.editedAt) || Boolean(message?.status);
  const inlineMeta = isDefault && !isStreaming && hasMeta ? /*#__PURE__*/_jsx(ChatMessageInlineMeta, {}) : undefined;
  return /*#__PURE__*/_jsx(MessageGroupComponent, _extends({
    messageId: id
  }, slotProps?.messageGroup ?? {}, {
    children: /*#__PURE__*/_jsxs(MessageRootComponent, _extends({
      messageId: id
    }, slotProps?.messageRoot ?? {}, {
      children: [/*#__PURE__*/_jsx(MessageAvatarComponent, _extends({}, slotProps?.messageAvatar ?? {})), /*#__PURE__*/_jsx(MessageContentComponent, _extends({}, slotProps?.messageContent ?? {}, {
        afterContent: inlineMeta
      })), !isDefault && /*#__PURE__*/_jsx(MessageMetaComponent, _extends({}, slotProps?.messageMeta ?? {})), MessageActionsSlot && /*#__PURE__*/_jsx(ChatMessageActions, _extends({}, slotProps?.messageActions ?? {}, {
        children: /*#__PURE__*/_jsx(MessageActionsSlot, {
          messageId: id
        })
      }))]
    }))
  }));
}
function DefaultConversationHeader({
  slots,
  slotProps,
  features,
  onMenuClick,
  showMenuButton
}) {
  const localeText = useChatLocaleText();
  if (features?.conversationHeader === false) {
    return null;
  }
  const ConversationHeaderComponent = slots?.conversationHeader ?? ChatConversationHeader;
  const ConversationHeaderInfoComponent = slots?.conversationHeaderInfo ?? ChatConversationHeaderInfo;
  const ConversationTitleComponent = slots?.conversationTitle ?? ChatConversationTitle;
  const ConversationSubtitleComponent = slots?.conversationSubtitle ?? ChatConversationSubtitle;
  const ConversationHeaderActionsComponent = slots?.conversationHeaderActions ?? ChatConversationHeaderActions;
  return /*#__PURE__*/_jsxs(ConversationHeaderComponent, _extends({}, slotProps?.conversationHeader ?? {}, {
    children: [showMenuButton && /*#__PURE__*/_jsx(Tooltip, {
      title: localeText.conversationHeaderMenuLabel,
      children: /*#__PURE__*/_jsx(IconButton, {
        size: "small",
        "aria-label": localeText.conversationHeaderMenuLabel,
        onClick: onMenuClick,
        sx: {
          mr: 1
        },
        children: /*#__PURE__*/_jsx(DefaultMenuIcon, {})
      })
    }), /*#__PURE__*/_jsxs(ConversationHeaderInfoComponent, _extends({}, slotProps?.conversationHeaderInfo ?? {}, {
      children: [/*#__PURE__*/_jsx(ConversationTitleComponent, _extends({}, slotProps?.conversationTitle ?? {})), /*#__PURE__*/_jsx(ConversationSubtitleComponent, _extends({}, slotProps?.conversationSubtitle ?? {}))]
    })), /*#__PURE__*/_jsx(ConversationHeaderActionsComponent, _extends({}, slotProps?.conversationHeaderActions ?? {}))]
  }));
}
function DefaultComposer({
  slots,
  slotProps,
  features
}) {
  const contextVariant = useChatVariant();
  const variant = slotProps?.composerRoot?.variant ?? contextVariant;
  const showAttachments = features?.attachments !== false;
  const showHelperText = features?.helperText !== false;
  const attachmentConfig = typeof features?.attachments === 'object' ? features.attachments : undefined;
  const ComposerRootComponent = slots?.composerRoot ?? ChatComposer;
  const ComposerInputComponent = slots?.composerInput ?? ChatComposerTextArea;
  const ComposerToolbarComponent = slots?.composerToolbar ?? ChatComposerToolbar;
  const ComposerSendButtonComponent = slots?.composerSendButton ?? ChatComposerSendButton;
  const ComposerAttachButtonComponent = slots?.composerAttachButton ?? ChatComposerAttachButton;
  const ComposerAttachmentListComponent = slots?.composerAttachmentList ?? ChatComposerAttachmentList;
  const ComposerHelperTextComponent = slots?.composerHelperText ?? ChatComposerHelperText;
  const localeText = useChatLocaleText();
  if (variant === 'compact') {
    return /*#__PURE__*/_jsxs(ComposerRootComponent, _extends({
      variant: "compact",
      attachmentConfig: attachmentConfig
    }, slotProps?.composerRoot ?? {}, {
      children: [showAttachments && /*#__PURE__*/_jsx(ComposerAttachmentListComponent, _extends({}, slotProps?.composerAttachmentList ?? {})), showAttachments && /*#__PURE__*/_jsx(ComposerAttachButtonComponent, _extends({
        "aria-label": localeText.composerAttachButtonLabel
      }, slotProps?.composerAttachButton ?? {}, {
        children: /*#__PURE__*/_jsx(DefaultAttachIcon, {})
      })), /*#__PURE__*/_jsx(ComposerInputComponent, _extends({
        maxRows: 5,
        placeholder: localeText.composerInputPlaceholder
      }, slotProps?.composerInput ?? {})), /*#__PURE__*/_jsx(ComposerSendButtonComponent, _extends({
        "aria-label": localeText.composerSendButtonLabel
      }, slotProps?.composerSendButton ?? {}, {
        children: /*#__PURE__*/_jsx(DefaultSendIcon, {})
      }))]
    }));
  }
  return /*#__PURE__*/_jsxs(ComposerRootComponent, _extends({
    attachmentConfig: attachmentConfig
  }, slotProps?.composerRoot ?? {}, {
    children: [showAttachments && /*#__PURE__*/_jsx(ComposerAttachmentListComponent, _extends({}, slotProps?.composerAttachmentList ?? {})), /*#__PURE__*/_jsx(ComposerInputComponent, _extends({
      placeholder: localeText.composerInputPlaceholder
    }, slotProps?.composerInput ?? {})), showHelperText && /*#__PURE__*/_jsx(ComposerHelperTextComponent, _extends({}, slotProps?.composerHelperText ?? {})), /*#__PURE__*/_jsxs(ComposerToolbarComponent, _extends({}, slotProps?.composerToolbar ?? {}, {
      children: [showAttachments && /*#__PURE__*/_jsx(ComposerAttachButtonComponent, _extends({
        "aria-label": localeText.composerAttachButtonLabel
      }, slotProps?.composerAttachButton ?? {}, {
        children: /*#__PURE__*/_jsx(DefaultAttachIcon, {})
      })), /*#__PURE__*/_jsx(ComposerSendButtonComponent, _extends({
        "aria-label": localeText.composerSendButtonLabel
      }, slotProps?.composerSendButton ?? {}, {
        children: /*#__PURE__*/_jsx(DefaultSendIcon, {})
      }))]
    }))]
  }));
}
export function ChatBoxContent(props) {
  const {
    variant,
    slots,
    slotProps,
    features,
    rootRef,
    layoutClassName,
    conversationsPaneClassName,
    threadPaneClassName,
    suggestions,
    suggestionsAutoSubmit
  } = props;
  const showScrollToBottom = features?.scrollToBottom !== false;
  const showSuggestions = features?.suggestions !== false && !!suggestions && suggestions.length > 0;
  const autoScrollProp = features?.autoScroll ?? true;
  const isNarrow = useContainerNarrow(rootRef);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const messageIds = useMessageIds();
  const conversations = useConversations();
  const localeText = useChatLocaleText();
  const hasConversationList = conversations.length > 0;
  const handleMenuClick = React.useCallback(() => {
    setDrawerOpen(true);
  }, []);
  const handleDrawerClose = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);
  const ScrollToBottomComponent = slots?.scrollToBottom ?? ChatScrollToBottomAffordance;
  const ConversationListComponent = slots?.conversationList ?? ChatConversationList;
  const MessageListComponent = slots?.messageList ?? ChatMessageList;
  const SuggestionsComponent = slots?.suggestions ?? ChatSuggestions;

  // Use refs so renderItem is stable and doesn't cause the virtualized list
  // to re-render every time a new object reference is passed for slots/slotProps.
  const slotsRef = React.useRef(slots);
  const slotPropsRef = React.useRef(slotProps);
  const featuresRef = React.useRef(features);
  slotsRef.current = slots;
  slotPropsRef.current = slotProps;
  featuresRef.current = features;
  const renderItem = React.useCallback(({
    id
  }) => /*#__PURE__*/_jsx(DefaultMessageItem, {
    id: id,
    slots: slotsRef.current,
    slotProps: slotPropsRef.current,
    features: featuresRef.current
  }, id), []);
  return /*#__PURE__*/_jsxs(ChatLayout, {
    className: layoutClassName,
    style: {
      flex: 1,
      minHeight: 0
    },
    slotProps: {
      conversationsPane: _extends({}, conversationsPaneClassName ? {
        className: conversationsPaneClassName
      } : {}, {
        style: {}
      }),
      threadPane: _extends({}, threadPaneClassName ? {
        className: threadPaneClassName
      } : {}, {
        style: {
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      })
    },
    children: [hasConversationList && /*#__PURE__*/_jsx(ConversationListComponent, _extends({
      variant: variant
    }, slotProps?.conversationList ?? {})), hasConversationList && isNarrow && /*#__PURE__*/_jsx(Drawer, {
      open: drawerOpen,
      onClose: handleDrawerClose,
      slotProps: {
        paper: {
          sx: {
            width: 'var(--ChatBox-conversationListWidth, 260px)',
            maxWidth: '80vw'
          }
        }
      },
      children: /*#__PURE__*/_jsx(ConversationListComponent, _extends({
        variant: variant
      }, slotProps?.conversationList ?? {}, {
        slotProps: _extends({}, slotProps?.conversationList?.slotProps, {
          item: params => {
            const externalSlotProps = slotProps?.conversationList?.slotProps?.item;
            const externalProps = typeof externalSlotProps === 'function' ? externalSlotProps(params) : externalSlotProps;
            return _extends({}, externalProps, {
              onClick: event => {
                externalProps?.onClick?.(event);
                handleDrawerClose();
              }
            });
          }
        })
      }))
    }), /*#__PURE__*/_jsxs(ChatConversation, {
      children: [/*#__PURE__*/_jsx(DefaultConversationHeader, {
        slots: slots,
        slotProps: slotProps,
        features: features,
        onMenuClick: handleMenuClick,
        showMenuButton: hasConversationList && isNarrow
      }), /*#__PURE__*/_jsx(MessageListComponent, _extends({
        renderItem: renderItem,
        items: messageIds,
        autoScroll: autoScrollProp,
        overlay: /*#__PURE__*/_jsxs(React.Fragment, {
          children: [messageIds.length === 0 && !showSuggestions && /*#__PURE__*/_jsxs(ChatBoxEmptyState, {
            children: [/*#__PURE__*/_jsx(ChatBoxEmptyStateIcon, {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: 1.5,
              strokeLinecap: "round",
              strokeLinejoin: "round",
              "aria-hidden": "true",
              children: /*#__PURE__*/_jsx("path", {
                d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              })
            }), /*#__PURE__*/_jsx(ChatBoxEmptyStateTitle, {
              children: localeText.threadNoMessagesLabel
            }), /*#__PURE__*/_jsx(ChatBoxEmptyStateHelper, {
              children: localeText.threadNoMessagesHelperText
            })]
          }), showSuggestions && messageIds.length === 0 && /*#__PURE__*/_jsx(SuggestionsComponent, _extends({
            suggestions: suggestions,
            autoSubmit: suggestionsAutoSubmit
          }, slotProps?.suggestions ?? {})), showScrollToBottom && /*#__PURE__*/_jsx(ScrollToBottomComponent, _extends({}, slotProps?.scrollToBottom ?? {}))]
        })
      }, slotProps?.messageList ?? {})), /*#__PURE__*/_jsx(DefaultComposer, {
        slots: slots,
        slotProps: slotProps,
        features: features
      })]
    })]
  });
}